/**
 * Hook for budget alerts and notifications
 */

import { useMemo } from 'react';
import type { BudgetAlert } from '../types/budget';

interface AlertThreshold {
  percent: number;
  severity: 'low' | 'medium' | 'high';
  type: 'warning' | 'danger' | 'over';
}

const DEFAULT_THRESHOLDS: AlertThreshold[] = [
  { percent: 75, severity: 'low', type: 'warning' },
  { percent: 90, severity: 'medium', type: 'danger' },
  { percent: 100, severity: 'high', type: 'over' },
];

interface CategoryData {
  categoryName: string;
  percentUsed: number;
  spent: number;
  budgeted: number;
}

export function useBudgetAlerts(
  categories: CategoryData[],
  thresholds: AlertThreshold[] = DEFAULT_THRESHOLDS
): {
  alerts: BudgetAlert[];
  criticalCount: number;
  warningCount: number;
  hasAlerts: boolean;
} {
  const alerts = useMemo(() => {
    const generatedAlerts: BudgetAlert[] = [];

    categories.forEach(category => {
      // Find applicable threshold
      const applicableThreshold = [...thresholds]
        .sort((a, b) => b.percent - a.percent)
        .find(t => category.percentUsed >= t.percent);

      if (applicableThreshold) {
        let message = '';
        
        if (applicableThreshold.type === 'over') {
          const overAmount = category.spent - category.budgeted;
          message = `${category.categoryName} ultrapassou o orçamento em R$ ${overAmount.toFixed(2)}`;
        } else if (applicableThreshold.type === 'danger') {
          message = `${category.categoryName} está próximo do limite (${category.percentUsed.toFixed(0)}%)`;
        } else {
          message = `${category.categoryName} atingiu ${category.percentUsed.toFixed(0)}% do orçamento`;
        }

        generatedAlerts.push({
          id: `alert-${category.categoryName}-${Date.now()}`,
          budget_id: 'current',
          category_id: category.categoryName,
          type: applicableThreshold.type === 'over' ? 'overspent' : 'warning',
          severity: applicableThreshold.severity,
          message,
          threshold_percent: applicableThreshold.percent,
          current_percent: category.percentUsed,
          created_at: new Date().toISOString(),
          acknowledged: false,
        });
      }
    });

    return generatedAlerts.sort((a, b) => {
      const severityOrder = { high: 3, medium: 2, low: 1 };
      return severityOrder[b.severity] - severityOrder[a.severity];
    });
  }, [categories, thresholds]);

  const criticalCount = useMemo(
    () => alerts.filter(a => a.severity === 'high').length,
    [alerts]
  );

  const warningCount = useMemo(
    () => alerts.filter(a => a.severity === 'medium' || a.severity === 'low').length,
    [alerts]
  );

  return {
    alerts,
    criticalCount,
    warningCount,
    hasAlerts: alerts.length > 0,
  };
}
